import { APITypesV1 } from "@cur8/api-client";
import {
  getImperialConversion,
  getPreferImperial,
  ImperialConversion,
  MetricConfig,
} from "./measurementConfig";

// Conversion definitions (display-only — API always receives SI values)

export type ConversionDef = {
  // Labels for each input field shown in imperial mode
  fields: ReadonlyArray<{ name: string; unit: string }>;
  // SI value → display values (one per field)
  toDisplay: (si: number) => number[];
  // Display values → SI value
  toSI: (display: number[]) => number;
  // Short label shown on the toggle button
  label: string;
};

// Rounds the total BEFORE splitting, so a remainder that rounds up to the base
// carries. Flooring first rendered 182.8 cm as "5 ft 12 in".
function splitCompound(total: number, base: number): number[] {
  const tenths = Math.round(total * 10);
  const baseTenths = base * 10;
  return [Math.floor(tenths / baseTenths), (tenths % baseTenths) / 10];
}

export const CONVERSION_DEFS: Record<ImperialConversion, ConversionDef> = {
  lbs: {
    fields: [{ name: "value", unit: "lbs" }],
    label: "lbs",
    toDisplay: (kg) => [round1(kg * 2.20462)],
    toSI: ([lbs]) => round1(lbs / 2.20462),
  },
  ft_in: {
    fields: [
      { name: "ft", unit: "ft" },
      { name: "in", unit: "in" },
    ],
    label: "ft / in",
    toDisplay: (cm) => splitCompound(cm / 2.54, 12),
    toSI: ([ft, inches]) => round1((ft * 12 + inches) * 2.54),
  },
};

// Explicit per-metric override wins; otherwise the country default.
export function getEffectiveConversion(
  config: MetricConfig,
  countryCode: string | undefined,
  showImperialOverride: boolean | undefined
): ConversionDef | null {
  const imperialConv = getImperialConversion(config, countryCode);
  if (!imperialConv) {
    return null;
  }
  const showImperial =
    showImperialOverride ?? getPreferImperial(config, countryCode);
  return showImperial ? CONVERSION_DEFS[imperialConv] : null;
}

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function isOutOfRange(
  value: number,
  range: APITypesV1.RiskRange
): boolean {
  for (const interval of range.riskIntervals) {
    if (
      interval.riskLevel === APITypesV1.RiskType.Risk ||
      interval.riskLevel === APITypesV1.RiskType.HighRisk ||
      interval.riskLevel === APITypesV1.RiskType.ModerateRisk ||
      interval.riskLevel === APITypesV1.RiskType.ImmediateRisk
    ) {
      const start = interval.start.value;
      const end = interval.end.value;
      const startInclusive = interval.start.inclusive ?? true;
      const endInclusive = interval.end.inclusive ?? true;

      const aboveStart =
        start == null || (startInclusive ? value >= start : value > start);
      const belowEnd =
        end == null || (endInclusive ? value <= end : value < end);

      if (aboveStart && belowEnd) {
        return true;
      }
    }
  }
  return false;
}
